import React, { useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { i18n } from './main';
import AppLayout from './components/AppLayout';
import { useAuthenticatedApp, useTokenRegistration } from '@origo/auth';
import { Loader, Notice } from '@origo/origo-ui';
import { AccessDeniedScreen } from './components/AccessDeniedScreen';
import { useAccessErrorScreen } from './hooks/useAccessErrorScreen';
import defaultSettings from './config/defaultSettings.json';
import { createPlanregisterClient, PlanregisterClientProvider } from '@origo/planregister';
import { ToastProvider } from '@origo/origo-components';
import { MapViewProvider } from '@origo/map-context';
import styles from './App.module.css';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: { retry: 2, staleTime: 30_000 },
        mutations: { retry: 0 },
    },
});

interface AppSettings {
    arealPlanUrl: string;
    /** ArcGIS WebMap-id som vises i kart-kolonnen. Valgfri — uten denne vises kartet med default-basemap. */
    webmap?: string;
    planbehandlingUrl?: string;
    dokumentUrl?: string;
    dispensasjonUrl?: string;
}

function normalizeFeatureServerUrl(raw: string): string {
    const trimmed = raw.trim().replace(/\/+$/, '');
    const stripped = trimmed.replace(/\/\d+$/, '');
    if (stripped !== trimmed) {
        console.warn(
            '[planforvaltning] arealPlanUrl pekte mot et enkelt-lag. Strippet trailing layer-indeks. ' +
            'Forventet base-URL er ".../FeatureServer" — ikke ".../FeatureServer/<n>".',
            { input: raw, normalized: stripped }
        );
    }
    return stripped;
}

const App: React.FC = () => {
    const { token, settings, isReady, signOut, accessError } = useAuthenticatedApp<AppSettings>('planforvaltning', defaultSettings as AppSettings);
    const arealPlanUrl = settings?.arealPlanUrl ? normalizeFeatureServerUrl(settings.arealPlanUrl) : '';
    // GDO-token registreres for Geodatas egne tjenester. AGOL-tjenester (services-eu1.arcgis.com)
    // håndteres automatisk via OAuth-credentialen som useAuthenticatedApp legger i IdentityManager.
    useTokenRegistration(token, 'https://services.geodataonline.no/arcgis/rest/services');

    const planregisterClient = useMemo(() => {
        console.info('[planforvaltning] planregister client', { serviceUrl: arealPlanUrl });
        return createPlanregisterClient({ serviceUrl: arealPlanUrl });
    }, [arealPlanUrl]);

    const accessErrorScreen = useAccessErrorScreen(accessError, signOut);

    if (accessErrorScreen) {
        return <AccessDeniedScreen {...accessErrorScreen} />;
    }

    if (!isReady) {
        return <Loader label="Laster…" />;
    }

    if (!arealPlanUrl) {
        return (
            <div className={styles.settingsError}>
                <Notice variant="danger">
                    <strong>Settings-feil:</strong> Portal-itemet <code>planforvaltning.settings</code>{' '}
                    mangler feltet <code>arealPlanUrl</code>. Legg til feltet i itemets data-JSON og last
                    siden på nytt.
                </Notice>
            </div>
        );
    }

    return (
        <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
                <PlanregisterClientProvider client={planregisterClient}>
                    <ToastProvider>
                        <MapViewProvider>
                            <AppLayout webMapId={settings?.webmap} />
                        </MapViewProvider>
                    </ToastProvider>
                </PlanregisterClientProvider>
            </I18nextProvider>
        </QueryClientProvider>
    );
};

export default App;
